export const APP_ROLES = [
  'user',
  'business_admin',
  'business_worker',
  'sales_head',
  'sales_rep',
  'global_admin',
] as const
export type AppRole = (typeof APP_ROLES)[number]

export const ADMIN_ROLES = ['global_admin'] as const satisfies readonly AppRole[]
export const SALES_ROLES = ['sales_head', 'sales_rep'] as const satisfies readonly AppRole[]
export const BUSINESS_ROLES = [
  'business_admin',
  'business_worker',
] as const satisfies readonly AppRole[]
export const STAFF_ROLES = [...ADMIN_ROLES, ...SALES_ROLES] as const

export type AdminRole = (typeof ADMIN_ROLES)[number]
export type SalesRole = (typeof SALES_ROLES)[number]
export type BusinessRole = (typeof BUSINESS_ROLES)[number]
export type StaffRole = AdminRole | SalesRole

export const isAdminRole = (r: AppRole): r is AdminRole =>
  (ADMIN_ROLES as readonly AppRole[]).includes(r)
export const isSalesRole = (r: AppRole): r is SalesRole =>
  (SALES_ROLES as readonly AppRole[]).includes(r)
export const isBusinessRole = (r: AppRole): r is BusinessRole =>
  (BUSINESS_ROLES as readonly AppRole[]).includes(r)
export const isStaffRole = (r: AppRole): r is StaffRole => isAdminRole(r) || isSalesRole(r)

/**
 * Which login portal an account belongs to. One-to-one with the user-
 * facing portals the wire convention exposes (`?role=admin|business|sales|user`):
 *
 *   - `'admin'` and `'sales'` → dashboard `/admin-login` today; the split
 *     exists so a dedicated sales portal can ship later without renaming
 *     the wire convention or migrating outstanding invite links.
 *   - `'business'` → dashboard `/login`.
 *   - `'user'` → mobile app deep-link recovery flow; not used on dashboard
 *     today, but kept in the enum so any future dashboard-mobile crossover
 *     (e.g. universal links) doesn't require expanding the wire format.
 */
export const ACCOUNT_TYPES = ['admin', 'business', 'sales', 'user'] as const
export type AccountType = (typeof ACCOUNT_TYPES)[number]

export function isAccountType(value: unknown): value is AccountType {
  return typeof value === 'string' && (ACCOUNT_TYPES as readonly string[]).includes(value)
}

/** Map a backend `AppRole` to the dashboard portal it belongs to. */
export function accountTypeForRole(role: AppRole): AccountType {
  switch (role) {
    case 'global_admin':
      return 'admin'
    case 'sales_head':
    case 'sales_rep':
      return 'sales'
    case 'business_admin':
    case 'business_worker':
      return 'business'
    case 'user':
      return 'user'
  }
}
